import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, FormHelperText, Stack } from '@mui/material'
import { createTeam } from 'api/teams'
import FormInput from 'components/form-elements/FormInput'
import FormSelect from 'components/form-elements/FormSelect'
import NotificationSys from 'components/NotificationSystem'
import { FIELD_IS_REQUIRED } from 'const'
import React, { useState } from 'react'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useMutation } from 'react-query'
import { PERSON_CENTERED_TEAM_HINT, TeamType, TeamTypeOptions } from 'types'
import zod, { TypeOf } from 'zod'
import { useTeams } from '../../hooks/useTeams'

export function TimelineTeamFormAdd({ onClose }: { onClose: () => void }) {
  const { onChangeTeam, onRefetchTeams } = useTeams()

  const formSchema = zod
    .object({
      name: zod
        .string()
        .min(1, FIELD_IS_REQUIRED)
        .max(50, 'Please enter a maximum of 50 characters'),
      // Starts empty, so the union carries '' until the user picks a type.
      type: zod.union([zod.nativeEnum(TeamType), zod.literal('')]).superRefine((val, ctx) => {
        if (!val) {
          ctx.addIssue({ code: zod.ZodIssueCode.custom, message: FIELD_IS_REQUIRED })
        }
      }),
      customTypeDescription: zod
        .string()
        .max(255, 'Please enter a maximum of 255 characters')
        .optional(),
    })
    // Mirrors the API rule: a Customized Team must describe its type.
    .superRefine((data, ctx) => {
      if (data.type === TeamType.CustomizedTeam && !data.customTypeDescription?.trim()) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          message: FIELD_IS_REQUIRED,
          path: ['customTypeDescription'],
        })
      }
    })

  type FormType = TypeOf<typeof formSchema>

  const methods = useForm<FormType>({
    mode: 'onChange' as const,
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      type: '',
      customTypeDescription: '',
    },
  })

  const selectedType = useWatch({ control: methods.control, name: 'type' })

  const createMutation = useMutation(createTeam)

  const [isLoading, setIsLoading] = useState(false)

  const onSubmitHandler = async (data: FormType) => {
    try {
      setIsLoading(true)
      const response = await createMutation.mutateAsync({
        name: data.name,
        // Validation above guarantees a type is selected by this point.
        type: data.type as TeamType,
        customTypeDescription:
          data.type === TeamType.CustomizedTeam ? data.customTypeDescription?.trim() : undefined,
      })
      const teamId = response.data.id

      await onRefetchTeams()
      await onChangeTeam(teamId)
      NotificationSys.showSuccess('Team successfully created')
      onClose()
    } catch (error) {
      console.error('Error creating team:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onSubmitHandler)}
        width="100%"
        noValidate
      >
        <Stack spacing={2}>
          <FormInput label="Name" name="name" fullWidth={true} />

          <Stack spacing={0.5}>
            <FormSelect name="type" label="Type" fullWidth options={TeamTypeOptions} />

            {selectedType === TeamType.PersonCenteredTeam && (
              <FormHelperText>{PERSON_CENTERED_TEAM_HINT}</FormHelperText>
            )}
          </Stack>

          {selectedType === TeamType.CustomizedTeam && (
            <FormInput label="Team Type Description" name="customTypeDescription" fullWidth />
          )}

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>

            <LoadingButton loading={isLoading} variant="contained" type="submit">
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </FormProvider>
  )
}
